import { FiMail, FiUser, FiUsers } from "react-icons/fi";
import type { MeetingAttendee } from "../../types/meeting.type";

interface AttendeeRowProps {
  attendee: MeetingAttendee;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (id: number, selected: boolean) => void;
}

const ATTENDANCE_STATUS_CONFIG: Record<
  MeetingAttendee["attendance_status"],
  { bgClass: string; label: string; textClass: string }
> = {
  pending: {
    label: "Pending",
    bgClass: "bg-gray-100",
    textClass: "text-gray-700",
  },
  attending: {
    label: "Attending",
    bgClass: "bg-green-100",
    textClass: "text-green-700",
  },
  not_attending: {
    label: "Not Attending",
    bgClass: "bg-red-100",
    textClass: "text-red-700",
  },
};

const formatDate = (iso: string | null): string => {
  if (!iso) {
    return "";
  }
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
};

export const AttendeeRow: React.FC<AttendeeRowProps> = ({
  attendee,
  selectable = false,
  selected = false,
  onSelectChange,
}) => {
  const statusConfig = ATTENDANCE_STATUS_CONFIG[attendee.attendance_status];

  return (
    <div
      data-testid={`attendee-row-${attendee.id}`}
      className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-b-0"
    >
      {selectable && (
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelectChange?.(attendee.id, e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          data-testid={`attendee-checkbox-${attendee.id}`}
        />
      )}
      <div className="flex-shrink-0 text-gray-400">
        {attendee.is_panel_member ? (
          <FiUsers className="h-4 w-4" />
        ) : (
          <FiUser className="h-4 w-4" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{attendee.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <FiMail className="h-3 w-3 text-gray-400 flex-shrink-0" />
          <p className="text-xs text-gray-500 truncate">{attendee.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {attendee.is_panel_member && (
          <span
            data-testid={`attendee-panel-badge-${attendee.id}`}
            className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
          >
            Panel
          </span>
        )}

        <span
          data-testid={`attendee-status-${attendee.id}`}
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.bgClass} ${statusConfig.textClass}`}
        >
          {statusConfig.label}
        </span>
      </div>

      {attendee.invitation_sent_at && (
        <div className="flex-shrink-0 text-xs text-gray-400">
          Invited {formatDate(attendee.invitation_sent_at)}
        </div>
      )}
    </div>
  );
};
